import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import { useMemo, useRef } from "react";
import { DrawerRef } from "@/uiCore/components/drawer";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app/useAppContext";
import { WorkflowType } from "@/pages/query/types";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobsSummary } from "@/helpers/apis";
import { getDateByEndOfDayWithoutTimeoffset, getDateByStartOfDayWithoutTimeoffset } from "@/components/dateRange/utils";
import { Button, Card, Stack } from "@altimateai/lego";
import { Drawer } from "@uicore";
import { getWorkflowNavigationPrefix, WorkflowTitle } from "@/pages/query/snowflake";
import { DateRange } from "@/components/dateRange";
import { CustomWorkloadDefinition } from "@/pages/query/custom-workloads/CustomWorkloadDefinition";
import { IconArrowLeft } from "@tabler/icons-react";

export const CustomWorkloadsRoot = () => {
  const { workflowId, runId } = useParams();
  const location = useLocation();
  const ref = useRef<DrawerRef | null>(null);
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { enableWarehouseSizeRecommendation } = useAppState();

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.TABLEAU
    : WorkflowType.CUSTOM;

  const { data: summaryData } = useQuery({
    queryKey: ["snowflake_job", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  // Compute default back URL based on current path depth
  const defaultBackUrl = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const prefix = getWorkflowNavigationPrefix(workflowType);
    if (parts.length > 3) {
      return `${prefix}/${encodeURIComponent(workflowId!)}`;
    }
    return prefix;
  }, [location.pathname, workflowType, workflowId]);

  return (
    <Card className="al-p-4">
      <Stack direction="column" className="gap-sm">
        <Stack align="center" justify={"between"} className={"al-w-full"}>
          <Stack align={"center"}>
            <Button
              title="Click to go back"
              asChild
              aria-label="Back"
              variant="ghost"
              size="icon"
            >
              <Link to={defaultBackUrl}>
                <IconArrowLeft />
              </Link>
            </Button>
            <WorkflowTitle
              workflowType={workflowType}
              workflowId={workflowId}
              runId={runId}
              tableauDashboardName={summaryData?.tableau_dashboard_name ?? ""}
            />
          </Stack>
          <Stack align={"center"}>
            {!enableWarehouseSizeRecommendation && (
              <Drawer
                buttonText="Definition"
                ref={ref}
                style={{
                  maxWidth: "none",
                }}
              >
                <CustomWorkloadDefinition workflowId={workflowId!} />
              </Drawer>
            )}
            <DateRange
              startDate={startDate}
              endDate={endDate}
              availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
              onDateRangeSelect={onUpdate}
            />
          </Stack>
        </Stack>
        <div />
        <Outlet />
      </Stack>
    </Card>
  );
}